import {fromJson} from '@github-ui/dotcom-schema/protobuf'
import {RepoIssuesDashboardContentResponseSchema} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import {queryFnFetchWithProtoSignal} from '@github-ui/react-core/future/query-fn-fetch'
import {getListItemIdentity} from '@github-ui/shared-dashboard-lists/shared/helpers/item-identity'

import {mergeDeferredIssues} from '../helpers/merge-deferred-issues'
import {repoDeferredToListItem} from '../helpers/repo-issues-deferred'
import {createRowNodeLoader} from './issues-dashboard-row-node'
import {repoIssuesToListItems} from '../helpers/repo-issues-dashboard-proto'

export type RepoRowItem = ReturnType<typeof repoIssuesToListItems>[number]

type RowsEnvelope = {
  payload?: {
    issuesIndexRowsData?: unknown
    milestoneIssuesRowsData?: unknown
  }
}

type RowsRouteId = keyof NonNullable<RowsEnvelope['payload']>

export function shouldFetchReactions(query: string): boolean {
  return /\bsort:reactions(?:-\S+)?\b/.test(query)
}

// Fetch and normalize enriched repository rows from an Issues surface's
// protobuf `_rows` endpoint.
async function fetchRepoRowNodesFromRoute({
  pathname,
  routeId,
  globalIds,
  fetchReactions,
}: {
  pathname: string
  routeId: RowsRouteId
  globalIds: string[]
  fetchReactions?: boolean
}): Promise<Map<string, RepoRowItem>> {
  const {json} = await queryFnFetchWithProtoSignal<RowsEnvelope>({
    routeId,
    queryName: 'rows',
    queryDeps: {
      pathname,
      searchParams: {
        ids: globalIds.join(','),
        fetch_reactions: fetchReactions?.toString() ?? null,
      },
    },
  })
  const rawRows = json.payload?.[routeId]
  if (rawRows === undefined) throw new Error(`Missing ${routeId} payload`)

  const rows = fromJson({
    schema: RepoIssuesDashboardContentResponseSchema,
    raw: rawRows,
    routeId,
    queryName: 'rows',
  })
  if (rows.error) throw new Error(rows.error)
  const deferred = rows.deferred.map(repoDeferredToListItem).filter(item => item !== undefined)
  const results = mergeDeferredIssues(repoIssuesToListItems(rows.results), deferred)

  const byIdentity = new Map<string, RepoRowItem>()
  for (const item of results) {
    byIdentity.set(getListItemIdentity(item), item)
  }
  return byIdentity
}

export function fetchRepoRowNodes(
  owner: string,
  repo: string,
  globalIds: string[],
  fetchReactions = false,
): Promise<Map<string, RepoRowItem>> {
  return fetchRepoRowNodesFromRoute({
    pathname: `/${owner}/${repo}/issues/_rows`,
    routeId: 'issuesIndexRowsData',
    globalIds,
    fetchReactions,
  })
}

export function fetchMilestoneRowNodes(
  owner: string,
  repo: string,
  milestoneNumber: number,
  globalIds: string[],
): Promise<Map<string, RepoRowItem>> {
  return fetchRepoRowNodesFromRoute({
    pathname: `/${owner}/${repo}/milestone/${milestoneNumber}/issues/_rows`,
    routeId: 'milestoneIssuesRowsData',
    globalIds,
  })
}

// The repository Issues index row-node loader, over GET /:owner/:repo/issues/_rows.
// Built per page (owner/repo are stable there) so it reuses the shared batching
// queue without sharing pending state with the dashboard loader.
export function createRepoRowNodeLoader(
  owner: string,
  repo: string,
  fetchReactions = false,
): {
  loadRowNode: (globalId: string) => Promise<RepoRowItem | undefined>
} {
  return createRowNodeLoader<RepoRowItem>(globalIds => fetchRepoRowNodes(owner, repo, globalIds, fetchReactions))
}

export function createMilestoneRowNodeLoader(
  owner: string,
  repo: string,
  milestoneNumber: number,
): {
  loadRowNode: (globalId: string) => Promise<RepoRowItem | undefined>
} {
  return createRowNodeLoader<RepoRowItem>(globalIds => fetchMilestoneRowNodes(owner, repo, milestoneNumber, globalIds))
}
